import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaTools,
  FaRocket,
  FaDatabase,
  FaMobile,
  FaCog,
  FaCode,
  FaArrowRight
} from 'react-icons/fa';
import { SERVICE_MEDIA } from '../constants/serviceMedia';
import './Services.css';

const Services = () => {
  useEffect(() => {
    // Add scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .fade-up, .scale-up, .slide-in-left, .slide-in-right, .slide-in-blur-left, .slide-in-blur-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const specializedServices = [
    {
      icon: <FaDatabase />,
      title: "Production Data Analytics & Reporting",
      description: "We turn your machine and line data into clear charts, trends and root-cause views—so your meetings are about decisions, not arguments over numbers.",
      features: [
        "KPI dashboards for production, quality and maintenance",
        "Drill-down analysis by line, product, shift and team",
        "Reporting packs for daily, weekly and monthly reviews",
        "Identification of chronic bottlenecks and hidden losses"
      ]
    },
    {
      icon: <FaTools />,
      title: "In-Circuit Test (ICT) Engineering",
      description: "Complete ICT solutions that validate your PCB at component level—before it reaches functional test or, worse, the field.",
      features: [
        "ICT-fixture-ready PCB design review and guidance",
        "Component presence, shorts, opens and value checks",
        "Collaboration on ICT test program development and tuning",
        "Support for scaling from prototype to high-volume production"
      ]
    },
    {
      icon: <FaCog />,
      title: "Functional Test (FCT) Systems",
      description: "FCT setups that make your product “think” it’s in the real world—so you can be confident it works before you ship it.",
      features: [
        "Functional validation of boards, modules and final products",
        "Load, communication and IO testing under realistic conditions",
        "End-of-line test benches integrated into your production flow",
        "Pass/fail criteria, result logging and label/barcode printing"
      ]
    },
    {
      icon: <FaRocket />,
      title: "Robotics & Special Purpose Machines",
      description: "We combine robots, custom fixtures and motion control into cells that take over repetitive, tiring or precision-critical work from human operators.",
      features: [
        "Robotic welding, pick-and-place and handling cells",
        "Custom SPMs for assembly, machining and testing",
        "Integration of machine vision for guidance and inspection",
        "Safety design that keeps operators protected and productive"
      ]
    },
    {
      icon: <FaMobile />,
      title: "Industrial IoT & Remote Monitoring",
      description: "We connect your existing machines—old and new—to secure gateways and dashboards so your team can see what’s happening from anywhere.",
      features: [
        "Gateway design for PLCs, meters and legacy equipment",
        "Monitoring of production, energy and critical parameters",
        "Alerts for breakdowns, quality drifts and abnormal usage",
        "Web and mobile access for maintenance and management"
      ]
    },
    {
      icon: <FaCode />,
      title: "Custom Automation & R&D Projects",
      description: "For problems that don’t fit standard products, we design one-off or low-volume automation systems—prototypes, pilot lines and proof-of-concept rigs.",
      features: [
        "Concept studies and feasibility validation",
        "Rapid prototyping of mechanisms, controls and software",
        "Upgrades and performance tuning of existing machines",
        "Technical consulting for long-term automation roadmap"
      ]
    }
  ];

  const processSteps = [
    {
      number: "1",
      title: "Understand & Capture Reality",
      description: "We visit your plant, study the process, listen to operators and collect data. The goal is simple: understand what’s really happening today, not just what’s written in SOPs."
    },
    {
      number: "2",
      title: "Concept & Solution Blueprint",
      description: "We propose one or more solution concepts—clear, visual and practical. You see how the automation will work, what it will touch and what results you can expect."
    },
    {
      number: "3",
      title: "Engineering, Build & Testing",
      description: "Our team designs the mechanical, electrical, control and software elements and tests them in-house or in a controlled environment until they behave like they should."
    },
    {
      number: "4",
      title: "On-Site Integration",
      description: "We install, integrate and commission the solution at your facility, coordinating with your production, maintenance and safety teams to minimize disruption."
    },
    {
      number: "5",
      title: "Training, Handover & Ongoing Support",
      description: "We train your operators and technicians, hand over documentation and stay available for tweaks, expansions and support as you scale up your use of the system."
    }
  ];

  return (
    <div className="services-page">
      <SEO
        title="Industrial Automation Services - SCADA, PLC, Robotics & PCB Test Jigs"
        description="Expert industrial automation services including SCADA systems, PLC programming, robotics integration, PCB test jigs (ICT/FCT), IoT monitoring, and custom automation solutions. Transform your manufacturing with proven engineering expertise."
        keywords="industrial automation services, SCADA systems, PLC programming, robotics integration, PCB test jigs, ICT test fixtures, FCT testing, industrial IoT, automation engineering, control systems, manufacturing automation, test automation, industrial software development, robolog automation services"
        ogImage="/dashboard.png"
      />
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content fade-up">
            <h1>Services That Turn Your Factory Problems Into Finished Solutions</h1>
            <p>
              We help manufacturers move from manual, unpredictable operations to engineered automation—using
              control systems, robotics, PCB test jigs and industrial software that are built for real shop-floor conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Core Service Areas</h2>
            <p className="section-subtitle">
              Everything you need to design, build and scale industrial automation—under one roof.
            </p>
          </div>

          <div className="services-grid">
            {/* Card 1 - Dashboard / Software */}
            <div className="service-card slide-in-blur-left">
              <div className="service-image with-photo">
                <img src={SERVICE_MEDIA.dashboard} alt="Industrial Software & Dashboard" />
                <div className="image-overlay"><h4>Live Factory Dashboards</h4></div>
              </div>
              <div className="service-content">
                <h3>Industrial Software & Shop-Floor Visibility</h3>
                <p className="service-hook">Get one clear view of your plant instead of 10 scattered Excel sheets.</p>
                <p>
                  We build industrial software that connects directly to your PLCs, test jigs, robots and sensors.
                  The result is simple, visual dashboards that show production, quality and downtime in real time—so your team can fix problems before they become losses.
                </p>
                <ul className="service-features">
                  <li>Real-time views of machines, lines and shifts</li>
                  <li>OEE, downtime and quality metrics in one place</li>
                  <li>Dashboards for operators, engineers and management</li>
                  <li>Alerts when performance, quality or energy go out of range</li>
                  <li>Integration with your current ERP, MES or in-house tools</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="btn btn-primary">Discuss a Monitoring Use Case</Link>
                </div>
              </div>
            </div>

            {/* Card 2 - Control Panels / Automation */}
            <div className="service-card fade-up">
              <div className="service-image with-photo">
                <img src={SERVICE_MEDIA.panel} alt="Automation Control Panel" />
                <div className="image-overlay"><h4>Control Panel & PLC</h4></div>
              </div>
              <div className="service-content">
                <h3>Automation & Control Engineering</h3>
                <p className="service-hook">The control systems that keep your machines running day after day.</p>
                <p>
                  We design and build industrial control systems for machines, lines and processes.
                  From PLC programs and HMI screens to panels and field commissioning, we make sure your automation is stable, safe and easy to maintain.
                </p>
                <ul className="service-features">
                  <li>PLC and HMI design aligned with your process</li>
                  <li>MCCs, drive panels and machine control panels</li>
                  <li>Field wiring, instruments and on-site commissioning</li>
                  <li>Safety circuits, interlocks and documentation that technicians can follow</li>
                  <li>Provision for future IIoT and data connectivity</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="btn btn-primary">Talk About an Automation Project</Link>
                </div>
              </div>
            </div>

            {/* Card 3 - PCB Test Jigs / Electronics */}
            <div className="service-card slide-in-blur-right">
              <div className="service-image with-photo">
                <img src={SERVICE_MEDIA.jig} alt="PCB Test Jig" />
                <div className="image-overlay"><h4>ICT / FCT Test Jigs</h4></div>
              </div>
              <div className="service-content">
                <h3>PCB Test Jigs & Functional Test Fixtures</h3>
                <p className="service-hook">When your PCB goes out untested, your customer becomes your tester.</p>
                <p>
                  We build ICT and FCT jigs for PCB and electronics manufacturers who need consistent, repeatable testing.
                  Our fixtures are engineered for high volumes, quick changeover and tight electrical performance—so you catch defects before your customer does.
                </p>
                <ul className="service-features">
                  <li>ICT and FCT jigs tailored to your PCB layout and connectors</li>
                  <li>Pneumatic or mechanical clamping for reliable contact</li>
                  <li>Support for multiple variants on the same base fixture</li>
                  <li>Seamless integration with your existing testers and software</li>
                  <li>Built-in provision for logging results and traceability</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="btn btn-primary">Review a PCB Test Requirement</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="specialized-services">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Specialized Solutions</h2>
            <p className="section-subtitle">
              Deep capabilities for problems that standard hardware and software can’t solve.
            </p>
          </div>

          <div className="specialized-grid">
            {specializedServices.map((service, index) => (
              <div
                key={index}
                className={`specialized-card fade-up`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="specialized-icon">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul>
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">How We Work With You</h2>
            <p className="section-subtitle">
              A simple, engineering-first approach that keeps you involved and in control.
            </p>
          </div>

          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className={`process-step fade-up`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="technology-stack">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Technology We Work With</h2>
            <p className="section-subtitle">
              Practical tools and platforms chosen for reliability, support and long-term maintainability.
            </p>
          </div>

          <div className="tech-categories">
            <div className="tech-category slide-in-blur-left">
              <h3>Software & Application Layer</h3>
              <p className="tech-intro">For dashboards, control apps, data processing and integrations.</p>
              <div className="tech-items">
                <span className="tech-item">Python</span>
                <span className="tech-item">Django / FastAPI</span>
                <span className="tech-item">C# / .NET</span>
                <span className="tech-item">Flutter</span>
                <span className="tech-item">JavaScript / TypeScript</span>
                <span className="tech-item">React</span>
                <span className="tech-item">Node.js</span>
              </div>
            </div>

            <div className="tech-category fade-up">
              <h3>Industrial IoT & Field Layer</h3>
              <p className="tech-intro">For talking directly to the machines and sensors on your shop floor.</p>
              <div className="tech-items">
                <span className="tech-item">ESP32 / embedded controllers</span>
                <span className="tech-item">Raspberry Pi / edge devices</span>
                <span className="tech-item">PLC integration (Siemens, Allen-Bradley, etc.)</span>
                <span className="tech-item">Industrial sensors, actuators and drives</span>
                <span className="tech-item">MQTT, Modbus, OPC UA communication</span>
                <span className="tech-item">Industrial gateways and protocol converters</span>
              </div>
            </div>

            <div className="tech-category slide-in-blur-right">
              <h3>Cloud, Storage & Databases</h3>
              <p className="tech-intro">For safely storing data and making it useful over time.</p>
              <div className="tech-items">
                <span className="tech-item">AWS and other cloud platforms</span>
                <span className="tech-item">PostgreSQL</span>
                <span className="tech-item">MySQL</span>
                <span className="tech-item">MongoDB</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="text-center scale-up">
            <h2>Ready To Walk Us Through Your Problem?</h2>
            <p>
              Whether you need a PCB test jig, a robotic cell, a monitoring system—or you’re not sure what you need yet—the first step is a conversation. Tell us what you build and where things are breaking down. We’ll help you see what automation can realistically fix.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Start a Project Discussion
                <FaArrowRight />
              </Link>
              <Link to="/services" className="btn btn-outline">
                View All Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
